import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import { Notification } from "@rio-cloud/rio-uikit";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { MonthNavigation } from "@components/Dashboard";
import SelectedFilters from "@components/SelectedFilters";
import KpiCard, { KpiCardDelta, KpiCardIcon } from "@components/Dashboard/KPICards/KpiCard";
import KpiInfoTooltip, { formatPeriodRange } from "@components/shared/KpiInfoTooltip";
import KpiComparisonTooltip from "@components/shared/KpiComparisonTooltip";
import KpiCardsSkeleton from "@components/Skeletons/KpiCardsSkeleton";
import HorizontalBarListSkeleton from "@components/Skeletons/HorizontalBarListSkeleton";
import TopVehiclesMaintenanceTableSkeleton from "@components/Skeletons/TopVehiclesMaintenanceTableSkeleton";
import DetailFormSkeleton from "@components/Skeletons/DetailFormSkeleton";
import MaintenanceAlertsCard from "@components/Maintenance/AlertsCard";
import HorizontalBarList, { HorizontalBarItem } from "@components/Maintenance/HorizontalBarList";
import TopVehiclesMaintenanceTable from "@components/Maintenance/TopVehiclesMaintenanceTable";
import MaintenanceTable from "@components/Maintenance/MaintenanceTable";
import MaintenanceDetail from "@components/Maintenance/MaintenanceDetail";
import {
  EMPTY_MAINTENANCE_SUMMARY,
  exportMaintenance,
  getMaintenanceAlertTotals,
  getMaintenanceByType,
  getMaintenanceSummary,
  getMaintenanceTransactionDetail,
  getMaintenanceTransactions,
  getTopMaintenanceSubcategories,
  getTopMaintenanceVehicles,
  MaintenanceAlertTotals,
  MaintenanceSummary,
  MaintenanceTransaction,
  MaintenanceTypeTotal,
  MaintenanceVehicleCost,
} from "@services/maintenance";
import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import { useAppDispatch } from "@store";
import { useIsTreeReady } from "@store/tmsAssetTree/tmsAssetTreeSlice";
import { useRequestFilters, useStoredFilterValues } from "@hooks/useStoredFilters";
import { useMonthNavigation } from "@hooks/useMonthNavigation";
import { useT } from "@hooks";
import { toKpiComparison } from "@utils/kpiComparison";

const PAGE_SIZE = 10;
const DETAIL_SKELETON_ROWS = 7;
const TOP_LIMIT = 5;
const BY_TYPE_COLOR = "#5C9EBE";
const SUBCATEGORY_COLOR = "#F2907F";
const SUBCATEGORY_LABEL_WIDTH = 130;
const KPI_CARDS = 3;
const KPI_SKELETON_FOOTER_EXTRA_WIDTHS = [undefined, 125];
const SUBCATEGORIES_TITLE_SKELETON_WIDTH = 140;

interface MaintenanceDetailModal {
  open: boolean;
  loading: boolean;
  data: MaintenanceTransaction | null;
}

const CLOSED_DETAIL_MODAL: MaintenanceDetailModal = { open: false, loading: false, data: null };

const formatAmount = (value: number) =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatCurrency = (value: number) => `R$ ${formatAmount(value)}`;

const formatInteger = (value: number) => value.toLocaleString("pt-BR");

const resolveMomentLocale = (locale: string): string => {
  if (locale.startsWith("en")) return "en-gb";
  if (locale.startsWith("es")) return "es";
  return "pt-br";
};

const MaintenanceScreen = () => {
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);
  const dispatch = useAppDispatch();

  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();
  const kpiPeriodRange = formatPeriodRange(startDate, endDate);
  const isTreeReady = useIsTreeReady();
  const storedFilters = useStoredFilterValues();

  const { showDateRangePicker, setShowDateRangePicker, currentMonth, goToPrevMonth, goToNextMonth } =
    useMonthNavigation({ startDate, endDate, momentLocale, dispatch });

  const requestFilters = useRequestFilters(storedFilters, startDate, endDate);

  const [summary, setSummary] = useState<MaintenanceSummary>(EMPTY_MAINTENANCE_SUMMARY);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [byType, setByType] = useState<MaintenanceTypeTotal[]>([]);
  const [isLoadingByType, setIsLoadingByType] = useState(true);
  const [subcategories, setSubcategories] = useState<HorizontalBarItem[]>([]);
  const [isLoadingSubcategories, setIsLoadingSubcategories] = useState(true);
  const [vehicles, setVehicles] = useState<MaintenanceVehicleCost[]>([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [alertTotals, setAlertTotals] = useState<MaintenanceAlertTotals | undefined>(undefined);
  const [transactions, setTransactions] = useState<MaintenanceTransaction[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingTable, setIsLoadingTable] = useState(true);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [detailModal, setDetailModal] = useState<MaintenanceDetailModal>(CLOSED_DETAIL_MODAL);

  const hasPeriod = Boolean(startDate && endDate);

  useEffect(() => {
    let cancelled = false;
    if (!hasPeriod || !isTreeReady) return;
    setIsLoadingSummary(true);
    setIsLoadingByType(true);
    setIsLoadingSubcategories(true);
    setIsLoadingVehicles(true);
    setAlertTotals(undefined);

    getMaintenanceSummary(requestFilters)
      .then((result) => {
        if (!cancelled) setSummary(result);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSummary(false);
      });
    getMaintenanceByType(requestFilters)
      .then((items) => {
        if (!cancelled) setByType(items);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingByType(false);
      });
    getTopMaintenanceSubcategories(requestFilters, TOP_LIMIT)
      .then((items) => {
        if (cancelled) return;
        setSubcategories(items.map((item) => ({ label: item.subcategory, value: item.amount })));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSubcategories(false);
      });
    getTopMaintenanceVehicles(requestFilters, TOP_LIMIT)
      .then((items) => {
        if (!cancelled) setVehicles(items);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingVehicles(false);
      });
    getMaintenanceAlertTotals(requestFilters).then((totals) => {
      if (!cancelled) setAlertTotals(totals);
    });

    return () => {
      cancelled = true;
    };
  }, [hasPeriod, isTreeReady, requestFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [requestFilters]);

  useEffect(() => {
    let cancelled = false;
    if (!hasPeriod || !isTreeReady) return;
    setIsLoadingTable(true);
    getMaintenanceTransactions(requestFilters, currentPage - 1, PAGE_SIZE)
      .then((result) => {
        if (cancelled) return;
        setTransactions(result.content);
        setTotalPages(Math.max(1, result.totalPages));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasPeriod, isTreeReady, requestFilters, currentPage, tableRefreshKey]);

  const byTypeItems: HorizontalBarItem[] = byType.map((item) => ({
    label: t(`maintenance.type.${item.type}`),
    value: item.amount,
  }));

  const handleRefresh = useCallback(() => {
    setTableRefreshKey((key) => key + 1);
  }, []);

  const handleDownload = useCallback(async () => {
    try {
      await exportMaintenance(requestFilters);
      Notification.success(t("maintenance.notification.exportSuccess"));
    } catch {
      Notification.error(t("maintenance.notification.exportError"));
    }
  }, [requestFilters, t]);

  const handleViewDetail = useCallback(
    async (id: string) => {
      setDetailModal({ open: true, loading: true, data: null });
      const detail = await getMaintenanceTransactionDetail(id).catch(() => null);

      if (!detail) {
        Notification.error(t("maintenance.notification.detailError"));
        setDetailModal(CLOSED_DETAIL_MODAL);
        return;
      }

      setDetailModal((prev) => ({ ...prev, loading: false, data: detail }));
    },
    [t]
  );

  const handleCloseDetail = useCallback(() => setDetailModal(CLOSED_DETAIL_MODAL), []);

  const renderDetailBody = () => {
    if (detailModal.loading) return <DetailFormSkeleton rows={DETAIL_SKELETON_ROWS} />;
    return detailModal.data ? <MaintenanceDetail transaction={detailModal.data} /> : null;
  };

  return (
    <div className="padding-10 padding-20-sm display-flex flex-column gap-10 gap-20-sm min-height-100pct">
      <MonthNavigation
        endDate={endDate}
        startDate={startDate}
        currentMonth={currentMonth}
        onPrevMonth={goToPrevMonth}
        onNextMonth={goToNextMonth}
        onEndDateChange={(date) => dispatch(setEndDate(date))}
        onStartDateChange={(date) => dispatch(setStartDate(date))}
        showDateRangePicker={showDateRangePicker}
        onClick={() => setShowDateRangePicker(!showDateRangePicker)}
        onCloseDateRangePicker={() => setShowDateRangePicker(false)}
      />
      <SelectedFilters requestFilters={requestFilters} />

      {isLoadingSummary ? (
        <KpiCardsSkeleton
          cards={KPI_CARDS}
          className="display-grid gap-10 gap-20-sm grid-cols-1 grid-cols-3-md"
          style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
          contentGapClassName="gap-20"
          footerExtraWidths={KPI_SKELETON_FOOTER_EXTRA_WIDTHS}
        />
      ) : (
        <div
          className="display-grid gap-10 gap-20-sm grid-cols-1 grid-cols-3-md"
          style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
        >
          <KpiCard
            label={
              <>
                {t("maintenance.kpi.totalSpent")}
                <KpiInfoTooltip
                  title={t("maintenance.kpi.totalSpent")}
                  description={t("maintenance.kpi.totalSpent.tooltip")}
                  lines={[formatCurrency(summary.totalSpent.value)]}
                  periodRange={kpiPeriodRange}
                />
              </>
            }
            value={formatAmount(summary.totalSpent.value)}
            icon={<KpiCardIcon />}
            contentGapClassName="gap-20"
            footer={
              <KpiComparisonTooltip comparison={toKpiComparison(summary.totalSpent)} formatValue={formatCurrency}>
                <KpiCardDelta deltaPercentage={summary.totalSpent.variationPercent} vsLabel={t("kpi.vsLastMonth")} />
              </KpiComparisonTooltip>
            }
          />
          <KpiCard
            label={
              <>
                {t("maintenance.kpi.avgCostPerKm")}
                <KpiInfoTooltip
                  title={t("maintenance.kpi.avgCostPerKm")}
                  description={t("maintenance.kpi.avgCostPerKm.tooltip")}
                  lines={[formatCurrency(summary.averageCostPerKm.value)]}
                  periodRange={kpiPeriodRange}
                />
              </>
            }
            value={formatAmount(summary.averageCostPerKm.value)}
            icon={<KpiCardIcon />}
            contentGapClassName="gap-20"
            footerGapClassName="gap-15"
            footer={
              <>
                <span className="display-flex align-items-center">
                  <KpiComparisonTooltip
                    comparison={toKpiComparison(summary.averageCostPerKm)}
                    formatValue={formatCurrency}
                  >
                    <KpiCardDelta
                      deltaPercentage={summary.averageCostPerKm.variationPercent}
                      vsLabel={t("kpi.vsLastMonth")}
                    />
                  </KpiComparisonTooltip>
                </span>
                <span className="text-color-gray">
                  <span className="text-medium text-color-success">
                    {formatInteger(summary.averageCostPerKm.kmDriven)}
                  </span>{" "}
                  {t("maintenance.kpi.kmDriven")}
                </span>
              </>
            }
          />
          <KpiCard
            label={
              <>
                {t("maintenance.kpi.avgCostPerVehicle")}
                <KpiInfoTooltip
                  title={t("maintenance.kpi.avgCostPerVehicle")}
                  description={t("maintenance.kpi.avgCostPerVehicle.tooltip")}
                  lines={[formatCurrency(summary.averageCostPerVehicle.value)]}
                  periodRange={kpiPeriodRange}
                  placement="bottom-end"
                />
              </>
            }
            value={formatAmount(summary.averageCostPerVehicle.value)}
            icon={<KpiCardIcon />}
            contentGapClassName="gap-20"
            footer={
              <KpiComparisonTooltip
                comparison={toKpiComparison(summary.averageCostPerVehicle)}
                formatValue={formatCurrency}
                placement="bottom-end"
              >
                <KpiCardDelta
                  deltaPercentage={summary.averageCostPerVehicle.variationPercent}
                  vsLabel={t("kpi.vsLastMonth")}
                />
              </KpiComparisonTooltip>
            }
          />
        </div>
      )}

      <div
        className="display-grid gap-10 gap-20-sm grid-cols-1 grid-cols-2-lg"
        style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)" }}
      >
        <MaintenanceAlertsCard totals={alertTotals} />
        {isLoadingByType ? (
          <HorizontalBarListSkeleton items={3} />
        ) : (
          <HorizontalBarList title={t("maintenance.chart.byType")} items={byTypeItems} color={BY_TYPE_COLOR} />
        )}
      </div>

      <div
        className="display-grid gap-10 gap-20-sm grid-cols-1 grid-cols-2-lg"
        style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)" }}
      >
        {isLoadingSubcategories ? (
          <HorizontalBarListSkeleton
            labelWidth={SUBCATEGORY_LABEL_WIDTH}
            titleWidth={SUBCATEGORIES_TITLE_SKELETON_WIDTH}
          />
        ) : (
          <HorizontalBarList
            title={t("maintenance.chart.topSubcategories")}
            items={subcategories}
            color={SUBCATEGORY_COLOR}
            labelWidth={SUBCATEGORY_LABEL_WIDTH}
          />
        )}
        {isLoadingVehicles ? <TopVehiclesMaintenanceTableSkeleton /> : <TopVehiclesMaintenanceTable items={vehicles} />}
      </div>

      <MaintenanceTable
        data={transactions}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        isLoading={isLoadingTable}
        onRefresh={handleRefresh}
        onDownload={handleDownload}
        onViewDetail={handleViewDetail}
      />

      <Dialog
        show={detailModal.open}
        title={<span className="text-medium text-size-16">{t("maintenance.dialog.detailTitle")}</span>}
        body={renderDetailBody()}
        footer={
          <div className="display-flex flex-wrap gap-10 justify-content-end">
            <button type="button" className="btn btn-default width-100pct-xs" onClick={handleCloseDetail}>
              {t("common.action.close")}
            </button>
          </div>
        }
        onClose={handleCloseDetail}
        showCloseButton
        bsSize="md"
      />
    </div>
  );
};

export default MaintenanceScreen;
